/**
 * PATH: packages/ui/src/kanban/components/quickCapture.ts
 * FILE_ID: UI-KANBAN-QUICKCAPTURE-001
 * PURPOSE: Pure classifier for the card-face quick-capture input — decide
 *   whether what the operator typed is a DATE (→ set the card due date) or a
 *   NOTE (→ add to the card's comment thread).
 * WHY: Operator 2026-06-24: "I should be able to hover over the card and start
 *   typing the date or something or notes and it just gets added to it."
 *   Confirmed behaviour: a bare date sets the due date; anything else is a
 *   progress note. Reuses the description detector's date parser and adds the
 *   natural forms an operator stares at on the card face — "30 Jun" / "Jun 30"
 *   and the numeric "06-30" / "30/6" the due pills render in.
 * FLOW:
 *   ┌──────┐  parse date  ┌───────────────┐  date-only?  ┌──────┐
 *   │ text │ ───────────▶ │ word/iso/M-D/  │ ───────────▶ │ due  │
 *   │      │              │ month-name hit │   else ───▶  │ note │
 *   └──────┘              └───────────────┘              └──────┘
 * DEPENDENCIES:
 *   - ./card-detail/descriptionActions: findFirstDuePhrase + toIsoDate (the one
 *     date parser of record — we extend, never fork it).
 * RELATED:
 *   - ./KanbanCardQuickCapture.tsx: the input that applies the result.
 */
import { findFirstDuePhrase, toIsoDate } from './card-detail/descriptionActions'

export type QuickCaptureResult =
  | { kind: 'due'; iso: string; label: string }
  | { kind: 'action'; iso: string; label: string }
  | { kind: 'note'; body: string }

interface DateHit {
  raw: string
  iso: string
  label: string
}

const MONTHS: Record<string, number> = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
}

const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Mirror the description detector's `dueLabel` so a quick-captured due reads
 *  identically to a detected one ("Today" / "Tomorrow" / "Wed 24 Jun"). */
function labelFor(d: Date, today: Date): string {
  const days = Math.round(
    (startOfDay(d).getTime() - startOfDay(today).getTime()) / 86_400_000,
  )
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  return `${WD_SHORT[d.getDay()]} ${d.getDate()} ${MON_SHORT[d.getMonth()]}`
}

/** Resolve a (day, month) with no explicit year to a concrete date: this year,
 *  rolled to next year when it has already passed (so "30 Jun" typed in July
 *  means next June, not a due date in the past). */
function resolveDayMonth(
  day: number,
  mon: number,
  explicitYear: number | null,
  today: Date,
): Date | null {
  let year = explicitYear ?? today.getFullYear()
  let d = new Date(year, mon, day)
  // Guard against rollover (e.g. 31 Feb → 3 Mar): reject impossible dates.
  if (d.getMonth() !== mon || d.getDate() !== day) return null
  if (explicitYear === null && startOfDay(d) < today) {
    year += 1
    d = new Date(year, mon, day)
  }
  return d
}

/** "30 jun", "30 june 2026", "jun 30", "june 30, 2026". Unambiguous because the
 *  month is a name — no locale guessing required. */
function findMonthNameDate(text: string, now: Date): DateHit | null {
  const today = startOfDay(now)

  // day-first: "30 jun [2026]"
  const dayFirst = text.match(/\b(\d{1,2})\s+([a-z]{3,9})\b(?:\s*,?\s*(\d{4}))?/i)
  if (dayFirst && MONTHS[dayFirst[2].toLowerCase()] !== undefined) {
    const d = resolveDayMonth(
      Number(dayFirst[1]),
      MONTHS[dayFirst[2].toLowerCase()],
      dayFirst[3] ? Number(dayFirst[3]) : null,
      today,
    )
    if (d) return { raw: dayFirst[0], iso: toIsoDate(d), label: labelFor(d, today) }
  }

  // month-first: "jun 30[, 2026]"
  const monthFirst = text.match(/\b([a-z]{3,9})\s+(\d{1,2})\b(?:\s*,?\s*(\d{4}))?/i)
  if (monthFirst && MONTHS[monthFirst[1].toLowerCase()] !== undefined) {
    const d = resolveDayMonth(
      Number(monthFirst[2]),
      MONTHS[monthFirst[1].toLowerCase()],
      monthFirst[3] ? Number(monthFirst[3]) : null,
      today,
    )
    if (d) return { raw: monthFirst[0], iso: toIsoDate(d), label: labelFor(d, today) }
  }

  return null
}

/** Numeric short date — "06-30", "30/6", "5.30.2026". Only accepted when ONE
 *  component is unambiguously the day (13..31); a fully ambiguous "6/7" (could
 *  be Jun 7 or Jul 6) is intentionally rejected → it falls through to a note so
 *  we never silently set the WRONG due date. Matches the MM-DD the due pills
 *  render (operator types what they see). */
function findNumericShortDate(text: string, now: Date): DateHit | null {
  const today = startOfDay(now)
  const m = text.match(/\b(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?\b/)
  if (!m) return null
  const a = Number(m[1])
  const b = Number(m[2])
  let day: number
  let mon: number
  if (a >= 13 && a <= 31 && b >= 1 && b <= 12) {
    day = a
    mon = b - 1
  } else if (b >= 13 && b <= 31 && a >= 1 && a <= 12) {
    day = b
    mon = a - 1
  } else {
    return null // ambiguous (both ≤ 12) or invalid — let it be a note
  }
  let year: number | null = null
  if (m[3]) year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])
  const d = resolveDayMonth(day, mon, year, today)
  if (!d) return null
  return { raw: m[0], iso: toIsoDate(d), label: labelFor(d, today) }
}

// Lead words that commonly prefix a bare date ("due Friday", "by 30 Jun") and
// must not, on their own, make the input look like prose.
const DATE_LEAD_RE = /\b(due date|due|by|deadline|on|set|for)\b/gi

// FEAT-2026-07-02-KANBAN-CARD-TYPE-TO-CAPTURE: an explicit action-date form
// ("action 22 jul", "ad 22/7", "action date fri") targets the card's
// action_date instead of due_date; the operator's boards run on the
// action-date framework ("I can't type things like action date").
const ACTION_LEAD_RE = /^\s*(action date|action|ad)\b/i

/**
 * Classify quick-capture input. Returns:
 *   - { kind: 'action' } when the text starts with action/action date/ad and
 *     the rest is essentially a date. The card's ACTION date should be set.
 *   - { kind: 'due' }  when the text is ESSENTIALLY just a date (optionally
 *     prefixed by due/by/deadline). The card's due date should be set.
 *   - { kind: 'note' } for anything with real content (even if it embeds a
 *     date) — saved verbatim as a progress note.
 *   - null for empty / whitespace-only input.
 */
export function classifyQuickCapture(
  text: string,
  now: Date = new Date(),
): QuickCaptureResult | null {
  const trimmed = (text ?? '').trim()
  if (!trimmed) return null

  // Explicit action-date lead: strip it, then run the SAME date pipeline as
  // due (one parser of record). "action 22 jul" -> action date 22 Jul; an
  // action lead followed by prose ("action call Bob") stays a note.
  const actionLead = trimmed.match(ACTION_LEAD_RE)
  const dateText = actionLead ? trimmed.slice(actionLead[0].length).trim() : trimmed

  const hit =
    findFirstDuePhrase(dateText, now) ??
    findMonthNameDate(dateText, now) ??
    findNumericShortDate(dateText, now)

  if (hit) {
    const remainder = dateText
      .replace(hit.raw, ' ')
      .replace(DATE_LEAD_RE, ' ')
      .replace(/[:\-\u2013\u2014,.]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (remainder.length === 0) {
      return actionLead
        ? { kind: 'action', iso: hit.iso, label: hit.label }
        : { kind: 'due', iso: hit.iso, label: hit.label }
    }
  }

  return { kind: 'note', body: trimmed }
}
